import { useQuery } from '@tanstack/react-query';
import {
  GitlabClient,
  GitlabProvisioningGroupsAndProjectsPage,
} from '../httpClients/GitlabClient';
import { QUERY_KEYS } from './consts';

export const GITLAB_PROVISIONING_PAGE_SIZE = 10;

/** PAT + pasted connect URL the stateless Select call needs (server re-derives scope). */
export type GitlabProvisioningSelectContext = {
  accessToken: string;
  gitlabUrl: string;
};

/**
 * Fetch one server-merged, paginated page of the Select tree: groups (alpha) then
 * projects (alpha) over one concatenated stream. `parentGroupId` undefined = top
 * level; set = its subgroups + repos; `search` set = scope-wide flat matches.
 * @param connect - PAT + pasted URL from the connect step; undefined disables the query.
 * @param parentGroupId - Group to list children of; undefined = top level.
 * @param search - Search term; scope-wide flat match across both types when set.
 * @param page - 1-based page number.
 * @param cursor - Opaque deep-paging cursor for this page (undefined on page 1 or an older BE); takes precedence over `page` server-side.
 * @param options - Extra query options (e.g. `enabled`).
 * @returns Query of a single merged page.
 */
export const useGitlabProvisioningGroupsAndProjectsQuery = (
  connect: GitlabProvisioningSelectContext | undefined,
  parentGroupId: number | undefined,
  search: string,
  page: number,
  cursor: string | undefined,
  options: { enabled?: boolean } = {},
) =>
  useQuery<GitlabProvisioningGroupsAndProjectsPage>({
    // `cursor` is deliberately NOT in the key: it's a deterministic function of
    // (gitlabUrl, parentGroupId, search, page), all already keyed, so keying on it
    // too would split one logical page into two cache entries. It rides as a request
    // param only. Safe as long as `page` stays in the key to distinguish each page.
    queryKey: [
      QUERY_KEYS.GITLAB_PROVISION_GROUPS_AND_PROJECTS,
      connect?.gitlabUrl,
      parentGroupId,
      search,
      page,
    ],
    queryFn: () =>
      GitlabClient.getProvisioningGroupsAndProjects({
        accessToken: connect!.accessToken,
        gitlabUrl: connect!.gitlabUrl,
        parentGroupId,
        page,
        cursor,
        perPage: GITLAB_PROVISIONING_PAGE_SIZE,
        search: search || undefined,
      }),
    // Live "what can this PAT see" data, and the PAT is deliberately not in the
    // queryKey — refetch on mount + evict on unmount so a new PAT can't reuse a
    // prior token's cached scopes.
    staleTime: 0,
    gcTime: 0,
    ...options,
    enabled: (options.enabled ?? true) && connect !== undefined,
  });
